import { initializeApp } from "firebase/app";
import { getFirestore, collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, onSnapshot, serverTimestamp, setDoc, arrayUnion, limit } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup, updateProfile } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const firebaseConfig = {
  apiKey: "AIzaSyClLFODv3shaJhom20Sq-f-ke7lSA1GU-8",
  authDomain: "flux-ai-assist.firebaseapp.com",
  projectId: "flux-ai-assist",
  storageBucket: "flux-ai-assist.firebasestorage.app",
  messagingSenderId: "4830292530",
  appId: "1:4830292530:web:c02465ed95c3a415e52a2b",
  measurementId: "G-SJDY2PJKPF"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const storage = getStorage(app);

// Collections
const COLLECTIONS = {
  USERS: 'users',
  CLIENTS: 'clients',
  INVOICES: 'invoices',
  SETTINGS: 'settings',
  PAYMENTS: 'payments'
};

// Auth Helper Functions
export const authHelpers = {
  signIn: (email, password) => signInWithEmailAndPassword(auth, email, password),
  signUp: (email, password) => createUserWithEmailAndPassword(auth, email, password),
  signInWithGoogle: () => {
    const provider = new GoogleAuthProvider()
    return signInWithPopup(auth, provider)
  },
  signOut: () => signOut(auth),
  resetPassword: (email) => sendPasswordResetEmail(auth, email),
  onAuthChange: (callback) => onAuthStateChanged(auth, callback),
  getCurrentUser: () => auth.currentUser,
  updateUserProfile: (user, data) => updateProfile(user, data)
};

// Settings CRUD
export const settingsService = {
  async getSettings(userId) {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().settings || null;
    }
    return null;
  },

  async getPublicSettings(userId) {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().settings || null;
    }
    return null;
  },

  async updateSettings(userId, settings) {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(userRef, {
      settings,
      updatedAt: serverTimestamp()
    });
  },

  async createUserSettings(userId, settings) {
    await setDoc(doc(db, COLLECTIONS.USERS, userId), {
      settings,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
};

// Logo Upload Service (Cloudinary version)
export const logoService = {
  async uploadLogo(userId, file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 400;
          const MAX_HEIGHT = 400;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob and upload to Cloudinary
          try {
            canvas.toBlob(async (blob) => {
              if (!blob) {
                reject(new Error('Canvas to Blob conversion failed'));
                return;
              }

              const formData = new FormData();
              formData.append('file', blob);
              // Note: You must create an unsigned upload preset named "ncinq_preset" in your Cloudinary settings
              formData.append('upload_preset', 'ncinq_preset'); 

              const response = await fetch('https://api.cloudinary.com/v1_1/dhnsuq8zb/image/upload', {
                method: 'POST',
                body: formData,
              });

              if (!response.ok) {
                console.error("Cloudinary upload failed", await response.text());
                reject(new Error('Failed to upload image to Cloudinary'));
                return;
              }

              const data = await response.json();
              resolve(data.secure_url);
            }, 'image/png', 0.8);
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = error => reject(error);
        img.src = event.target.result;
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  },

  async deleteLogo(userId, logoUrl) {
    // Cloudinary images normally require a signed request from a backend to delete.
    // We return true here so the UI can remove the reference from Firestore.
    return true;
  }
};

// Client CRUD Operations
export const clientService = {
  async getClients(userId) {
    const q = query(
      collection(db, COLLECTIONS.CLIENTS),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  },

  async getClient(userId, clientId) {
    const docRef = doc(db, COLLECTIONS.CLIENTS, clientId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  },

  async getPublicClient(clientId) {
    const docRef = doc(db, COLLECTIONS.CLIENTS, clientId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  },

  async addClient(userId, clientData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.CLIENTS), {
      userId,
      ...clientData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  },

  async updateClient(userId, clientId, clientData) {
    const docRef = doc(db, COLLECTIONS.CLIENTS, clientId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      await updateDoc(docRef, {
        ...clientData,
        updatedAt: serverTimestamp()
      });
      return true;
    }
    return false;
  },

  async deleteClient(userId, clientId) {
    const docRef = doc(db, COLLECTIONS.CLIENTS, clientId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      await deleteDoc(docRef);
      return true;
    }
    return false;
  },

  subscribeToClients(userId, callback) {
    const q = query(
      collection(db, COLLECTIONS.CLIENTS),
      where('userId', '==', userId)
    );
    return onSnapshot(q, (snapshot) => {
      const clients = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      callback(clients);
    });
  }
};

// Invoice CRUD Operations
export const invoiceService = {
  async getInvoices(userId) {
    const q = query(
      collection(db, COLLECTIONS.INVOICES),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => {
        const dateA = a.createdAt?.toMillis?.() || 0;
        const dateB = b.createdAt?.toMillis?.() || 0;
        return dateB - dateA;
      });
  },

  async getInvoice(userId, invoiceId) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  },

  async getInvoiceByPublicId(publicId) {
    const q = query(
      collection(db, COLLECTIONS.INVOICES),
      where('publicId', '==', publicId)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  },

  async getPublicInvoice(invoiceId) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  },

  async getPublicClientInvoices(clientId) {
    const q = query(
      collection(db, COLLECTIONS.INVOICES),
      where('clientId', '==', clientId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => {
        const dateA = a.createdAt?.toMillis?.() || 0;
        const dateB = b.createdAt?.toMillis?.() || 0;
        return dateB - dateA;
      });
  },

  async addInvoice(userId, invoiceData) {
    const publicId = 'inv_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    const invoiceNumber = invoiceData.invoiceNumber || generateInvoiceNumber();

    const docRef = await addDoc(collection(db, COLLECTIONS.INVOICES), {
      userId,
      publicId,
      invoiceNumber,
      status: 'draft', // Default status
      ...invoiceData, // Overrides default if status is provided in invoiceData
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  },

  async updateInvoice(userId, invoiceId, invoiceData) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      await updateDoc(docRef, {
        ...invoiceData,
        updatedAt: serverTimestamp()
      });
      return true;
    }
    return false;
  },

  async deleteInvoice(userId, invoiceId) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      await deleteDoc(docRef);
      return true;
    }
    return false;
  },

  async sendInvoice(userId, invoiceId) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      await updateDoc(docRef, {
        status: 'sent',
        sentAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return true;
    }
    return false;
  },

  async markAsViewed(invoiceId, userId) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const invoice = docSnap.data();
      // Only mark as viewed if it's currently in 'sent' status
      // We don't want to overwrite 'paid' or 'partial' statuses
      if (invoice.status === 'sent') {
        await updateDoc(docRef, {
          status: 'viewed',
          viewedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        // Notify the admin (userId is passed or retrieved from invoice)
        // If userId is not passed, use invoice.userId
        const adminId = userId || invoice.userId;
        if (adminId) {
          await addDoc(collection(db, 'notifications'), {
            userId: adminId,
            message: `Client viewed Invoice ${invoice.invoiceNumber || '#' + invoiceId}`,
            type: 'info',
            link: `/invoices/${invoiceId}/view`,
            read: false,
            createdAt: serverTimestamp()
          });
        }
        return true;
      }
    }
    return false;
  },

  async markAsPaid(userId, invoiceId, amount = null) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      const invoice = docSnap.data();
      const paidAmount = amount || invoice.total;
      const newStatus = paidAmount >= invoice.total ? 'paid' : 'partial';

      await updateDoc(docRef, {
        status: newStatus,
        paidAt: serverTimestamp(),
        partialAmount: paidAmount,
        updatedAt: serverTimestamp(),
        payments: arrayUnion({
          amount: paidAmount,
          date: new Date(),
          recordedBy: userId,
          method: 'manual'
        })
      });
      return true;
    }
    return false;
  },

  async recordPayment(userId, invoiceId, paymentData) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().userId === userId) {
      const invoice = docSnap.data();
      const totalPaid = (invoice.partialAmount || 0) + paymentData.amount;
      const newStatus = totalPaid >= invoice.total ? 'paid' : 'partial';

      // Add payment record
      await addDoc(collection(db, COLLECTIONS.PAYMENTS), {
        invoiceId,
        userId,
        ...paymentData,
        createdAt: serverTimestamp()
      });

      // Update invoice
      await updateDoc(docRef, {
        status: newStatus,
        paidAt: serverTimestamp(),
        partialAmount: totalPaid,
        updatedAt: serverTimestamp(),
        payments: arrayUnion({
          amount: paymentData.amount,
          date: new Date(),
          recordedBy: userId
        })
      });
      return true;
    }
    return false;
  },

  async recordPublicPayment(invoiceId, paymentData) {
    const docRef = doc(db, COLLECTIONS.INVOICES, invoiceId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const invoice = docSnap.data();
      const totalPaid = (invoice.partialAmount || 0) + paymentData.amount;
      const newStatus = totalPaid >= invoice.total ? 'paid' : 'partial';

      // Add payment record (use invoice userId as the owner)
      await addDoc(collection(db, COLLECTIONS.PAYMENTS), {
        invoiceId,
        userId: invoice.userId,
        ...paymentData,
        createdAt: serverTimestamp(),
        isPublic: true
      });

      // Update invoice
      await updateDoc(docRef, {
        status: newStatus,
        paidAt: serverTimestamp(),
        partialAmount: totalPaid,
        updatedAt: serverTimestamp(),
        payments: arrayUnion({
          amount: paymentData.amount,
          date: new Date(),
          isPublic: true
        })
      });
      return true;
    }
    return false;
  },

  subscribeToInvoices(userId, callback) {
    const q = query(
      collection(db, COLLECTIONS.INVOICES),
      where('userId', '==', userId)
    );
    return onSnapshot(q, (snapshot) => {
      const invoices = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const dateA = a.createdAt?.toMillis?.() || 0;
          const dateB = b.createdAt?.toMillis?.() || 0;
          return dateB - dateA;
        });
      callback(invoices);
    });
  }
};

// Dashboard Statistics
export const dashboardService = {
  async getStats(userId) {
    const invoices = await invoiceService.getInvoices(userId);
    const clients = await clientService.getClients(userId);

    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    const paidThisMonth = invoices
      .filter(inv => inv.status === 'paid' && inv.paidAt)
      .reduce((sum, inv) => {
        const paidDate = inv.paidAt.toDate ? inv.paidAt.toDate() : new Date(inv.paidAt);
        if (paidDate.getMonth() === thisMonth && paidDate.getFullYear() === thisYear) {
          return sum + inv.total;
        }
        return sum;
      }, 0);

    const outstanding = invoices
      .filter(inv => ['sent', 'viewed', 'partial', 'overdue'].includes(inv.status))
      .reduce((sum, inv) => sum + (inv.total - (inv.partialAmount || 0)), 0);

    const overdue = invoices
      .filter(inv => inv.status === 'overdue')
      .reduce((sum, inv) => sum + inv.total, 0);

    return {
      paidThisMonth,
      outstanding,
      overdue,
      draftCount: invoices.filter(inv => inv.status === 'draft').length,
      pendingCount: invoices.filter(inv => ['sent', 'viewed', 'partial'].includes(inv.status)).length,
      totalClients: clients.length,
      totalInvoices: invoices.length
    };
  }
};

// Generate invoice number
function generateInvoiceNumber() {
  const year = new Date().getFullYear();
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `INV-${year}-${random}`;
}

// Notification Service
export const notificationService = {
  // Add a persistent notification
  addNotification: async (userId, notification) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId,
        message: notification.message,
        type: notification.type || 'info', // info, success, warning, error
        link: notification.link || null,
        read: false,
        createdAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error adding notification:', error)
      throw error // Allow caller to handle
    }
  },

  // Subscribe to real-time notifications for a user
  subscribeToNotifications: (userId, callback) => {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'), // Newest first
      limit(50)
    )

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert timestamp to date for UI
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }))
      callback(notifications)
    })
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      })
    } catch (error) {
      console.error('Error marking notification as read:', error)
      throw error
    }
  },

  // Mark all as read
  markAllAsRead: async (userId) => {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      )
      const snapshot = await getDocs(q)
      const updatePromises = snapshot.docs.map(doc =>
        updateDoc(doc.ref, { read: true })
      )
      await Promise.all(updatePromises)
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      throw error
    }
  }
};

export { db, auth, storage, COLLECTIONS };
